/**
 * @file This file contains the patient API functions.
 * @module api/patientApi
 * 
 * There are enpoints that get here from both the auth server and the healthcare server
 * most of these endpoint calls come out of the practitioner healthcare system but somce come from labiq sample management as well
 * 
 */

import { ENV } from '../../config/runtimeEnv';
import { getSessionUser } from "../../utils/auth/sessionUser";

//Types
import { User } from "../../types/admin/UserTypes";
import { Patient } from "../../types/healthcare/PatientTypes";
import { MedicalHistory } from "../../types/healthcare/MedicalHistoryTypes";
import {
	PatientNamesResponse,
	PatientOtherNameMutationResponse,
	PatientOtherNameSubmission,
} from "../../types/healthcare/PatientNameTypes";
import { withNormalisedDateOfBirth } from "../../utils/healthcare/ValidateDateOfBirth";
import type { UserUuid } from "../../types/identity";
import { http } from "../../lib/http/client";

const API_BASE_URL = ENV.VITE_HEALTHCARE_SERVER;
const HEALTHCARE_BASE_URL = ENV.VITE_HEALTHCARE_SERVER;



// CU-14ygtkj65jv — the operation moved into the shared DOB utility so the generic
// patient-target user routes apply the same rule. Behaviour here is unchanged.
const normalisePatientDobForApi = (patientData: Partial<Patient>): Partial<Patient> =>
	withNormalisedDateOfBirth(patientData as Partial<Patient> & { date_of_birth?: unknown });


///////////////////////////////////////////////////////standard patient routes from the auth server
// CU-86d438bbx: absent by default — that is what keeps the clinical patient pickers on the
// legacy patient-only response. Only a caller that guards customer rows itself may opt in.
export interface PatientListScopeOptions {
	listScope?: string;
	userType?: string;
}

export const fetchPatients = async (
	page: number,
	perPage: number,
	search?: string,
	sortField?: string,
	sortDirection?: 'asc' | 'desc',
	isActive?: boolean,
	scope?: PatientListScopeOptions
): Promise<{
	data: Patient[];
	total: number;
}> => {
	// `search` travels in the POST body, never the query string: nginx uses the
	// default combined log format, which writes `$request` — query string included —
	// into the access log, so a search term in `params` puts patient names, emails
	// and phone numbers in plaintext on disk. Same reasoning as the internal
	// name-search route in authentication/routes/api.php ("POST so the name never
	// lands in a URL or access log"). The non-identifying paging/sort/filter values
	// stay in `params`. Backwards-compatible in both directions: the backend reads
	// the term with `$request->input('search', '')`, which resolves from either.
	const response = await http.post(
		`${API_BASE_URL}/practitioner/getPatient`,
		{
			...(search ? { search } : {}),
		},
		{
			params: {
				page,
				per_page: perPage,
				...(sortField ? { sort_field: sortField } : {}),
				...(sortDirection ? { sort_direction: sortDirection } : {}),
				...(isActive !== undefined ? { is_active: isActive ? 1 : 0} : {}),
				// CU-86d438bbx: non-identifying, so they travel in params; `search` stays in the body.
				...(scope?.listScope ? { list_scope: scope.listScope } : {}),
				...(scope?.userType ? { user_type: scope.userType } : {}),
			},
		}
	);

	return {
		data: response.data.data,
		total: response.data.pagination.total_items,
	};
};
  

export const fetchSinglePatientByUuid = async (
	uuid: string
): Promise<{
	data: Patient;
}> => {
	const response = await http.get(`${API_BASE_URL}/practitioner/patientSimpleUuid/${uuid}`);
	return {
		data: response.data,
	};
};

export const fetchPatient = async (
	id: string
): Promise<{
	data: Patient;
}> => {
	const response = await http.get(`${API_BASE_URL}/practitioner/patient/${id}`, {
		params: { _: Date.now() },
	});
	return {
		data: response.data,
	};
};

/**
 * B2.2F (HI_010_005819): the canonical patient create/update responses are the
 * patient payload plus an additive, bounded `ihi_validation` outcome block that
 * is present only when a manually entered IHI candidate was evaluated
 * server-side. The block is response metadata, not Patient state — validate it
 * with `parseIhiValidationFeedback()` and never store or log it.
 */
export interface PatientMutationResponse extends Patient {
	ihi_validation?: unknown;
	// CU-14ygtkj98p4 (HI_015_006105): update-only offer; validate with parseCoreDemographicRevalidationOffer().
	core_demographic_ihi_revalidation?: unknown;
}

/**
 * The top-level `patientData` keys the canonical practitioner patient UPDATE
 * route accepts as client-editable input.
 *
 * Mirrors `PatientController::update()`'s `$userRules` and the `$userFields`
 * allow-list it applies through `Arr::only()`, plus the separately handled
 * `has_one_name_only` lifecycle key. Everything else a GET response carries —
 * `id`, `uuid`, timestamps, `is_active`, roles and the loaded clinical
 * relations — is response-only state the route ignores.
 * CU-14ygtkje5r3: user_type is server-owned and never sent; promotion has its own admin route.
 */
export const CANONICAL_PATIENT_UPDATE_FIELDS = [
	"first_name",
	"middle_name",
	"last_name",
	"username",
	"email",
	"phone",
	"address",
	"city",
	"state",
	"country",
	"postal_code",
	"profile_picture",
	"date_of_birth",
	"gender",
	"sex",
	"panel_key",
	"reagents",
	"is_anonymous",
	"weight",
	"height",
	"has_one_name_only",
] as const;

/**
 * The `patientData.patient_info` keys the canonical update route accepts as
 * client-editable input.
 *
 * Deliberately NARROWER than that route's validation rules. `ihi_status`,
 * `ihi_record_status` and `ihi_source` are accepted by validation but are
 * server-owned manual-IHI trust state that the controller strips before any
 * mutation, so sending them is meaningless and contradicts the trust boundary.
 * `ihi_number` stays: it is the manually entered candidate, and the server's
 * validation gate — not this client — decides whether it is persisted.
 *
 * Excluded because the server owns them and the B1.4-D route refuses or ignores
 * them: `user_id` (refused outright with a fixed 422), `id`, `created_at`,
 * `updated_at`, the `cliniko_*` triple, `source_system` / `external_id`, and the
 * seven HI-association fields in
 * `PatientHiNameAssociationDecision::PROHIBITED_FIELDS`.
 *
 * `blood_group`, `no_known_allergies` and `clinical_warnings` are excluded
 * because the canonical update route does not validate or accept them. See the
 * unsupported-field finding in the delivery report — this client does not
 * silently widen a backend contract.
 */
export const CANONICAL_PATIENT_INFO_UPDATE_FIELDS = [
	"medicare_number",
	"medicare_individual_ref_number",
	"medicare_valid_to",
	"dva_file_number",
	"ihi_number",
	"pronouns",
	"is_transgender",
	"medicare_gender_code",
	"deceased_date",
	"country_of_birth",
	"atsi",
	"interpreter_required",
	"occupation",
	"ethnicities",
	"additional_emails",
	"notes",
] as const;

/**
 * Copy only the allow-listed keys that are actually PRESENT on the source.
 *
 * Presence, not truthiness: the canonical route distinguishes an omitted key
 * from an explicitly submitted null throughout (`sometimes` rules, and
 * `array_key_exists` for `has_one_name_only` and the IHI clear). Emitting every
 * allow-listed key would turn an untouched field into an explicit clear — for
 * `ihi_number` that would retire the patient's HI-name associations. So an
 * absent key stays absent and an explicit null is preserved as a null.
 */
const pickPresentFields = <T extends object>(
	source: T,
	allowed: readonly string[]
): Record<string, unknown> => {
	const picked: Record<string, unknown> = {};

	for (const field of allowed) {
		if (Object.prototype.hasOwnProperty.call(source, field)) {
			picked[field] = (source as Record<string, unknown>)[field];
		}
	}

	return picked;
};

/**
 * Build the `patientData` body for the canonical practitioner patient update.
 *
 * Exported for direct contract coverage. The body is assembled field by field
 * from the two allow-lists above and is NEVER spread from the caller's object,
 * so no server-owned property can reach the request even though the modal's
 * form state is seeded straight from a `GET /practitioner/patient/{id}`
 * response. That round-trip is what made every save fail: the fetched
 * `patient_info` carries `user_id`, and the B1.4-D route refuses its mere
 * presence with a fixed value-free 422.
 *
 * The refusal is correct and stays. This is the client honouring it.
 *
 * `patient_info` is emitted only when it is a non-null OBJECT: a patient with no
 * patient_info row round-trips an explicit `null` out of the GET, and the route
 * has no "clear patient_info" meaning for it — it 422s on `sometimes|array`.
 * That container rule is separate from the nulls INSIDE a supplied object, which
 * are deliberate clears and are preserved.
 */
export const buildCanonicalPatientUpdatePayload = (
	patientData: Partial<Patient>
): Record<string, unknown> => {
	const payload = pickPresentFields(patientData, CANONICAL_PATIENT_UPDATE_FIELDS);

	const patientInfo = patientData.patient_info;

	if (patientInfo && typeof patientInfo === "object") {
		payload.patient_info = pickPresentFields(patientInfo, CANONICAL_PATIENT_INFO_UPDATE_FIELDS);
	}

	return payload;
};

export const updatePatient = async (patientId: UserUuid, patientData: Partial<Patient>): Promise<PatientMutationResponse> => {
	const user:User= getSessionUser()
	// DOB validation/normalisation runs first so an invalid date still rejects
	// the mutation before any request is built, then the allow-list boundary
	// drops every server-owned property. Applying it HERE covers both the
	// explicit Save and the debounce autosave, because both reach the wire
	// through this one function.
	const normalisedPatientData = normalisePatientDobForApi(patientData);
	const response = await http.put(`${API_BASE_URL}/practitioner/patient/${patientId}`, {
		patientData: buildCanonicalPatientUpdatePayload(normalisedPatientData),
		user_id: user.uuid,
	});
	return response.data;
};

export const activatePatient = async (patientId: UserUuid) => {
	const response = await http.put(`${API_BASE_URL}/practitioner/patient/${patientId}/activate`, {});
	return response.data;
};

export const deactivatePatient = async (patientId: UserUuid) => {
	const response = await http.delete(`${API_BASE_URL}/practitioner/patient/${patientId}`);
	return response.data;
};


export const createPatient = async (patientData: Partial<Patient>): Promise<PatientMutationResponse> => {
	const user:User= getSessionUser()
	const normalisedPatientData = normalisePatientDobForApi(patientData);
	const response = await http.post(`${API_BASE_URL}/practitioner/patient`, {
		patientData: normalisedPatientData,
		user_id: user.uuid,
	});
	return response.data;
};

// ─── Structured patient names (B1.4-C read + lifecycle, B1.4-D indication) ───
//
// Four authenticated practitioner routes on the authentication server. Patient
// scope is derived server-side from the authenticated practitioner's assigned
// patients, so no ownership value is ever sent. There is deliberately no
// hard-delete route: HI_015_005824 requires a superseded name to stay
// retrievable, so a name leaves the active set only by supersession.

/**
 * Build the request body for an other-name create or replacement.
 *
 * Exported for direct contract coverage. The body is assembled field by field
 * from the five accepted keys — never spread from the caller's object — so no
 * server-owned value can reach the request even if a caller's object carries
 * one. Refused server-owned state includes ownership, `name_type`,
 * `previous_role`, `source`, the lifecycle timestamps, `hi_associated_at`, the
 * association target/verified path and the derived `is_hi_associated`
 * indication.
 *
 * A one-name-only submission sends explicit nulls for the given and middle
 * names: the single name belongs in the family-name role, and the backend
 * refuses a non-null given or middle name in that state.
 */
export const buildPatientOtherNamePayload = (
	submission: PatientOtherNameSubmission
): Record<string, unknown> => {
	const isOneNameOnly = submission.is_one_name_only === true;

	const payload: Record<string, unknown> = {
		family_name: submission.family_name,
		given_name: isOneNameOnly ? null : submission.given_name ?? null,
		middle_name: isOneNameOnly ? null : submission.middle_name ?? null,
		is_one_name_only: isOneNameOnly,
	};

	// Omission and an explicit null both resolve to the bounded server default,
	// so the key is sent only when a context was actually chosen.
	if (submission.name_context !== undefined && submission.name_context !== null) {
		payload.name_context = submission.name_context;
	}

	return payload;
};

/** Read the bounded structured-name projection for one patient. */
export const fetchPatientNames = async (patientId: UserUuid): Promise<PatientNamesResponse> => {
	const response = await http.get(`${API_BASE_URL}/practitioner/patient/${patientId}/names`);
	return response.data;
};

/** Create one ACTIVE other name (HI_010_005809 / HI_015_005809). */
export const createPatientOtherName = async (
	patientId: UserUuid,
	submission: PatientOtherNameSubmission
): Promise<PatientOtherNameMutationResponse> => {
	const response = await http.post(
		`${API_BASE_URL}/practitioner/patient/${patientId}/other-names`,
		buildPatientOtherNamePayload(submission)
	);
	return response.data;
};

/**
 * Change an active other name through the supersede-and-replace route.
 *
 * The existing row is never overwritten in place: the server supersedes it and
 * creates its replacement in one transaction, so the old name stays retrievable
 * in history (HI_015_005824).
 */
export const replacePatientOtherName = async (
	patientId: UserUuid,
	patientNameId: number,
	submission: PatientOtherNameSubmission
): Promise<PatientOtherNameMutationResponse> => {
	const response = await http.put(
		`${API_BASE_URL}/practitioner/patient/${patientId}/other-names/${patientNameId}`,
		buildPatientOtherNamePayload(submission)
	);
	return response.data;
};

/**
 * Explicitly supersede an active other name without a replacement. The record is
 * retired, not deleted, and stays readable in the name history. This route takes
 * no request payload.
 */
export const supersedePatientOtherName = async (
	patientId: UserUuid,
	patientNameId: number
): Promise<PatientOtherNameMutationResponse> => {
	const response = await http.post(
		`${API_BASE_URL}/practitioner/patient/${patientId}/other-names/${patientNameId}/supersede`,
		{}
	);
	return response.data;
};

export const assignGeneCodes = async (patientId: UserUuid) => {
	const response = await http.post(`${API_BASE_URL}/practitioner/patient/${patientId}/assign-role`, {
		"role": "gene.codes"
	});
	return response.data;
};



////////////////////////////////////////////// patient sample management
export const fetchPatientsSimple = async (
	page: number,
	perPage: number
): Promise<{
	data: Patient[];
	total: number;
	currentPage: number;
	totalPages: number;
}> => {
	const response = await http.post(
		`${API_BASE_URL}/practitioner/patientSimple`,
		{
		},
		{
			params: {
				page,
				per_page: perPage,
			},
		}
	);

	return {
		data: response.data.data,
		total: response.data.pagination.total_items,
		currentPage: response.data.pagination.current_page,
		totalPages: response.data.pagination.total_pages,
	};
};


export const fetchPatientsByIds = async (ids: UserUuid[]): Promise<Patient[]> => {
	if (ids.length === 0) return [];
	const response = await http.post(
		`${API_BASE_URL}/practitioner/patients/bulk`,
		{ patientIds: ids }
	);
	return response.data.data;
};

export const fetchPatientSamples = async (patientEmail: string) => {
	const response = await http.get(
		`${HEALTHCARE_BASE_URL}/practitioner/patient/gene-samples-all/${patientEmail}`
	);
	return response.data;
};






////////////////////////////////////////////////Medical History endpoints in healthcare server


// Fetch all medical history records for a given patient
export const fetchMedicalHistories = async (patientId: UserUuid): Promise<MedicalHistory[]> => {
    const response = await http.get(`${HEALTHCARE_BASE_URL}/patients/${patientId}/medical-histories`);
    return response.data;
};

// Fetch a single medical history record by ID
export const fetchMedicalHistory = async (id: number): Promise<MedicalHistory> => {
    const response = await http.get(`${HEALTHCARE_BASE_URL}/patients/medical-histories/${id}`);
    return response.data;
};

// Create a new medical history record
export const createMedicalHistory = async (data: Partial<MedicalHistory>): Promise<MedicalHistory> => {
    const response = await http.post(`${HEALTHCARE_BASE_URL}/patients/medical-histories`, data);
    return response.data;
};

// Update an existing medical history record
export const updateMedicalHistory = async (id: number, data: Partial<MedicalHistory>): Promise<MedicalHistory> => {
    const response = await http.put(`${HEALTHCARE_BASE_URL}/patients/medical-histories/${id}`, data);
    return response.data;
};

// Delete a medical history record
export const deleteMedicalHistory = async (id: number): Promise<{ message: string }> => {
    const response = await http.delete(`${HEALTHCARE_BASE_URL}/patients/medical-histories/${id}`);
    return response.data;
};


/**
 * Medical History Statistics
 */
export interface MedicalHistoryStats {
    total_records: number;
    active_records: number;
    resolved_records: number;
    critical_records: number;
    records_by_type: Record<string, number>;
    records_by_severity: Record<string, number>;
    records_needing_review: number;
}

/**
 * Fetch medical history statistics for a patient
 */
export const fetchMedicalHistoryStats = async (patientId: UserUuid): Promise<MedicalHistoryStats> => {
    const response = await http.get(`${HEALTHCARE_BASE_URL}/patients/${patientId}/medical-histories/stats`);
    return response.data;
};

/**
 * Fetch active medical history records for a patient
 */
export const fetchActiveMedicalHistories = async (patientId: UserUuid): Promise<MedicalHistory[]> => {
    const response = await http.get(`${HEALTHCARE_BASE_URL}/patients/${patientId}/medical-histories/active`);
    return response.data;
};

/**
 * Fetch critical medical history records for a patient
 */
export const fetchCriticalMedicalHistories = async (patientId: UserUuid): Promise<MedicalHistory[]> => {
    const response = await http.get(`${HEALTHCARE_BASE_URL}/patients/${patientId}/medical-histories/critical`);
    return response.data;
};

/**
 * Fetch medical histories by category
 */
export const fetchMedicalHistoriesByCategory = async (patientId: UserUuid, types: string[]): Promise<MedicalHistory[]> => {
    const response = await http.post(`${HEALTHCARE_BASE_URL}/patients/${patientId}/medical-histories/by-category`, 
        { types }
    );
    return response.data;
};

/**
 * Mark a medical history record as resolved
 */
export const resolveMedicalHistory = async (historyId: number, notes?: string): Promise<MedicalHistory> => {
    const response = await http.post(`${HEALTHCARE_BASE_URL}/patients/medical-histories/${historyId}/resolve`, 
        { 
            date_resolved: new Date(),
            resolution_notes: notes
        }
    );
    return response.data;
};



/**
 * Fetch detailed medical history
 */
export const fetchMedicalHistoryDetail = async (historyId: number): Promise<MedicalHistory> => {
    const response = await http.get(`${HEALTHCARE_BASE_URL}/patients/medical-histories/${historyId}`);
    return response.data;
};

// CU-14ygtkjc8yy: a UC.035 review the merge attempt raised, as category labels only; never on the status endpoint.
export interface MergeIhiReviewOutcome {
	reason: string;
	requirement_id: string;
	severity: string;
}

export interface MergeInitiateResponse {
	success: boolean;
	message: string;
	primary_user_id: UserUuid;
	secondary_user_id: UserUuid;
	merge_id: number;
	ihi_review_outcomes?: MergeIhiReviewOutcome[];
}

export interface MergeServiceResult {
	success: boolean;
	merged: Record<string, number>;
	error: string | null;
}

export type MergeJobStatus = 'pending' | 'in_progress' | 'completed' | 'partial_failure' | 'failed';

export interface MergeStatusResponse {
	id: number;
	primary_user_id: UserUuid;
	secondary_user_id: UserUuid;
	requester_id: UserUuid;
	status: MergeJobStatus;
	// One entry per linked service plus `identity`, where the secondary's credential is closed.
	per_service_results: (Record<string, MergeServiceResult> & { identity?: MergeServiceResult }) | null;
	started_at: string | null;
	finished_at: string | null;
	created_at: string;
	updated_at: string;
}

export interface MergeAmendedSearchCriteria {
	medicare_card_number: string;
	family_name: string;
	given_name: string;
	date_of_birth: string;
	sex: string;
}

export interface MergeAmendedSearchRequest {
	amends_merge_id: number;
	criteria: MergeAmendedSearchCriteria;
}

export const mergePatients = async (
	primaryPatientId: UserUuid,
	secondaryPatientId: UserUuid,
	amendedSearch?: MergeAmendedSearchRequest
): Promise<MergeInitiateResponse> => {
		const response = await http.post(`${API_BASE_URL}/practitioner/patient/merge`, {
			primary_user_id: primaryPatientId,
			secondary_user_id: secondaryPatientId,
			requester_id: getSessionUser().uuid,
			...(amendedSearch ? { amended_search: amendedSearch } : {}),
		});
		return response.data;
};

export const getMergeStatus = async (mergeId: number): Promise<MergeStatusResponse> => {
	const response = await http.get(`${API_BASE_URL}/practitioner/patient/merge/${mergeId}`);
	return response.data;
};

// CU-86d3vn0k0: the indicator is always explicit and never inferred; when true the
// single name stays in the family-name role.
export const normalisePublicKitPatientPayload = <
	T extends {
		first_name?: string | null;
		middle_name?: string | null;
		last_name?: string | null;
		has_one_name_only?: unknown;
	}
>(
	patientData: T
): T & { has_one_name_only: boolean } => {
	const hasOneNameOnly = patientData.has_one_name_only === true;

	return {
		...patientData,
		has_one_name_only: hasOneNameOnly,
		...(hasOneNameOnly ? { first_name: "", middle_name: "" } : {}),
	};
};

// S9 (14ygtkj7ag0): the update flag is gone — the route refuses an existing patient
// account with 409/account_exists, so the public form only ever creates.
export const registerKitWithPatientInfo = async (
	sampleId: string,
	patientData: any,
	notes: string,
	customTestPackageIds: number[] = []
) => {
	const response = await http.post(
		`${API_BASE_URL}/public/register-kit`,
		{
			sample_id: sampleId,
			patient_data: normalisePublicKitPatientPayload(patientData),
			notes: notes,
			// The selected packages are the source of truth for test types — the
			// backend resolves them from each package's analysis_types.
			custom_test_package_ids: customTestPackageIds,
		},
		{
			headers: {
				'Content-Type': 'application/json',
			},
		}
	);
	return response.data;
};

// ─── Authenticated kit activation (S8, CU-14ygtkj7afy) ──────────────────────

/** The pre-fill projection the authenticated kit route returns for the caller's own record. */
export interface KitPrefill {
	first_name: string;
	middle_name: string | null;
	last_name: string;
	has_one_name_only: boolean;
	email: string;
	phone: string | null;
	address: string | null;
	city: string | null;
	state: string | null;
	country: string | null;
	postal_code: string | null;
	date_of_birth: string | null;
	gender: string | null;
	weight: string | null;
	height: string | null;
	patient_info: { medicare_number: string | null } | null;
}

/** 403 when the account lacks the `access-patient` scope — a non-patient cannot activate a kit. */
export const fetchKitPrefill = async (): Promise<KitPrefill> => {
	const response = await http.get(`${API_BASE_URL}/patient/kit-prefill`);
	// kit-prefill nests under `data`; kit-registration returns `patient` at the top level.
	return response.data.data;
};

/** A non-null `patient` IS the "this kit is yours" signal; someone else's kit nulls it rather than 403ing. */
export interface KitRegistrationStatus {
	success: boolean;
	exists: boolean;
	registered: boolean;
	patient: KitPrefill | null;
}

export const fetchKitRegistrationStatus = async (sampleId: string): Promise<KitRegistrationStatus> => {
	const response = await http.get(`${API_BASE_URL}/patient/kit-registration`, {
		params: { sample_id: sampleId },
	});
	return response.data;
};

/**
 * The only `patient_data` keys the authenticated kit route accepts.
 *
 * The route refuses the whole request with a 422 when a credential, name, email
 * or server-owned identity key is merely PRESENT — an explicit null or empty
 * string counts. So the body is assembled from this list and is NEVER spread
 * from the page's form state, which carries every one of the refused keys.
 */
export const AUTHENTICATED_KIT_PATIENT_FIELDS = [
	"phone",
	"address",
	"city",
	"state",
	"country",
	"postal_code",
	"date_of_birth",
	"gender",
	"weight",
	"height",
] as const;

/** Exported for direct contract coverage — this is the boundary AC6 asserts on. */
export const buildAuthenticatedKitPatientData = (
	patientData: Record<string, any>
): Record<string, unknown> => {
	const patientDataFields: Record<string, unknown> = {};

	for (const field of AUTHENTICATED_KIT_PATIENT_FIELDS) {
		patientDataFields[field] = patientData?.[field] ?? "";
	}

	patientDataFields.patient_info = {
		medicare_number: patientData?.patient_info?.medicare_number ?? "",
	};

	return patientDataFields;
};

export const registerKitAuthenticated = async (
	sampleId: string,
	patientData: Record<string, any>,
	notes: string,
	customTestPackageIds: number[] = []
): Promise<{ success: boolean; message?: string }> => {
	const response = await http.post(
		`${API_BASE_URL}/patient/register-kit`,
		{
			sample_id: sampleId,
			notes: notes,
			custom_test_package_ids: customTestPackageIds,
			patient_data: buildAuthenticatedKitPatientData(patientData),
		},
		{
			headers: {
				"Content-Type": "application/json",
			},
		}
	);
	return response.data;
};

// src/api/labiq/SampleApi.ts - Add these functions

/**
 * The patient projection the kit check used to return to any caller holding a
 * sample id. S6 made it null for an anonymous caller — it carried the name,
 * email, phone, address, date of birth and Medicare number of whoever the kit
 * belonged to — so every read of `patient` must be null-guarded.
 *
 * ⚠️ `tsconfig.json` sets `"strict": false`, so `| null` is documentary here and
 * the compiler will NOT reject an unguarded read. The runtime guard in
 * `RegisterKit` and its coverage are the enforcement.
 */
export interface KitRegistrationPatient {
	id?: UserUuid;
	first_name?: string;
	middle_name?: string;
	last_name?: string;
	has_one_name_only?: boolean;
	email?: string;
	phone?: string;
	address?: string;
	city?: string;
	state?: string;
	country?: string;
	postal_code?: string;
	date_of_birth?: string;
	gender?: string;
	weight?: string;
	height?: string;
	patient_info?: {
		medicare_number?: string;
		medicare_individual_ref_number?: string;
		medicare_valid_to?: string;
		ihi_number?: string;
	} | null;
}

export interface KitRegistrationCheckResponse {
	success: boolean;
	exists: boolean;
	registered: boolean;
	patient: KitRegistrationPatient | null;
}

export const checkKitRegistration = async (sampleId: string): Promise<KitRegistrationCheckResponse> => {
	const response = await http.get(
		`${API_BASE_URL}/public/check-kit-registration`,
		{
			params: { sample_id: sampleId }
		}
	);
	return response.data;
};

// CU-14ygtkjdr83: the route only invites the kit's server-resolved owner and refuses any patient_data.password key.
export const updateKitRegistration = async (
	sampleId: string
): Promise<{
	success: boolean;
	message: string;
}> => {
	const response = await http.post(
		`${API_BASE_URL}/public/update-kit-registration`,
		{
			sample_id: sampleId,
		}
	);
	return response.data;
};


export const assignOrderKits = async (orderData: {
	sales_order_id: number;
	shopify_order_id?: number;
	assignments: Array<{
		label_id: string;
		stock_label_id: number;
		product_name: string;
		firstName: string;
		lastName: string;
		email: string;
		phone?: string;
	}>;
}, token?: string): Promise<{
	success: boolean;
	message: string;
	data: any;
}> => {
	const response = await http.post(
		`${API_BASE_URL}/public/assign-order-kits`,
		orderData,
		{
			headers: {
				"Content-Type": "application/json",
				...(token ? { "X-Assignment-Token": token } : {}),
			},
		}
	);
	return response.data;
};

// ─── Patient Record Import (Medical Director XML / PDF) ──────────────────────

/** A mapping the importer could not make — produced by the backend's ImportWarningCollector. */
export interface ImportWarning {
	code: string;
	field: string;
	message: string;
}

export interface XmlImportJobStatus {
	import_uuid: string;
	mode: 'new_patient' | 'existing_patient';
	source_format: 'md_xml' | 'pdf';
	status: 'pending' | 'processing' | 'completed' | 'partial' | 'failed';
	patient_uuid: string | null;
	patient_name_snapshot: string | null;
	demographics_snapshot: {
		first_name: string | null;
		last_name: string | null;
		date_of_birth: string | null;
		sex: string | null;
		medicare_number: string | null;
		email: string | null;
		phone: string | null;
		address: string | null;
	} | null;
	sections_total: number;
	sections_completed: number;
	sections_failed: number;
	section_results: Record<string, { success: boolean; count: number; error: string | null; warnings?: ImportWarning[] }> | null;
	section_details: {
		results?: Array<{
			id: number;
			name: string;
			date: string | null;
			test_types: string[];
			category: string | null;
			status: string;
			is_abnormal: boolean;
			reviewed_by: string | null;
			requesting_doctor: string | null;
			lab_name: string | null;
			source_page?: number;
		}>;
		medications?: Array<{ id: number; drug: string; dose: string; frequency: string; active: boolean; source_page?: number }>;
		appointments?: Array<{ id: number; date: string; type: string; summary: string; source_page?: number }>;
		warnings?: ImportWarning[];
	} | null;
	error_summary: string | null;
	original_filename: string | null;
	created_at: string;
	completed_at: string | null;
	practitioner_contacts: Array<{
		first_name: string | null;
		last_name: string | null;
		specialty: string | null;
		provider_id: string | null;
		hpii_number: string | null;
		matched_user_uuid: string | null;
	}> | null;
}

export const submitXmlImport = async (
	file: File,
	mode: 'new_patient' | 'existing_patient',
	company: string,
	existingPatientUuid?: string,
): Promise<{ import_uuid: string; status: string; message: string }> => {
	const formData = new FormData();
	formData.append('file', file);
	formData.append('mode', mode);
	formData.append('company', company);
	if (mode === 'existing_patient' && existingPatientUuid) {
		formData.append('existing_patient_uuid', existingPatientUuid);
	}

	const response = await http.post(
		`${HEALTHCARE_BASE_URL}/practitioner/xml-import`,
		formData,
		{
			headers: {
				'Content-Type': 'multipart/form-data',
			},
		},
	);
	return response.data;
};

export const fetchXmlImportStatus = async (
	importUuid: string,
	company: string,
): Promise<XmlImportJobStatus> => {
	const response = await http.get(
		`${HEALTHCARE_BASE_URL}/practitioner/xml-import/${importUuid}`,
		{
			params: { company },
		},
	);
	return response.data;
};

export const exportPatientXml = async (
	patientUuid: string,
	patientInternalId: UserUuid,
	company: string,
): Promise<void> => {
	const response = await http.get(
		`${HEALTHCARE_BASE_URL}/practitioner/xml-export/${patientUuid}`,
		{
			params: { company, patient_internal_id: patientInternalId },
			responseType: 'blob',
		},
	);

	const blob    = new Blob([response.data], { type: 'application/xml' });
	const url     = window.URL.createObjectURL(blob);
	const anchor  = document.createElement('a');
	anchor.href   = url;
	anchor.setAttribute('download', `patient-${patientUuid}-medical-director.xml`);
	document.body.appendChild(anchor);
	anchor.click();
	anchor.remove();
	window.URL.revokeObjectURL(url);
};
